<template>
  <InfoContentContainer>
    <div>
      <SectionHeader text="Why do bird collisions happen?" />
      <p>
        Nearly a billion birds are killed each year in the U.S. from collisions
        with windows (<a
          href="https://bioone.org/journals/the-condor/volume-116/issue-1/CONDOR-13-090.1/Birdbuilding-collisions-in-the-United-States--Estimates-of-annual/10.1650/CONDOR-13-090.1.full"
          target="_blank"
          >Loss et al. 2014</a
        >). Birds cannot perceive architectural cues that indicate the presence
        of a window; rather, they see the reflection of vegetation, a clear
        space to fly through, or are drawn towards the glass by artificial light
        at night. Glass being invisible to birds leads birds to collide with
        glass, severely injuring or killing them.
      </p>
      <Photo
        source="/images/dead-bird-karlijn-van-houwelingen.jpg"
        caption="A Project Safe Flight volunteer taking a photo of a dead collision victim."
        credit="Photo: Karlijn van Houwelingen/DW"
      />
    </div>

    <div>
      <SectionHeader text="About dBird" />
      <p>
        dBird is an online crowd-sourced data collection tool that provides a
        way for the public to report incidents of dead and injured birds,
        helping to contextualize and guide research and advocacy efforts that
        aim to reduce human-caused hazards to birds.
      </p>
      <p>
        dBird was launched in 2014 by NYC Bird Alliance (formerly NYC Audubon)
        to complement data collected from Project Safe Flight volunteer
        collision monitoring, which has been in place since 1997. dBird and
        Project Safe Flight help conservationists understand the dynamics and
        drivers of collisions to develop policies and technologies that prevent
        collisions from occurring.
      </p>
      <p>
        To make dBird accessible to all of North America, in 2020, Jim and Birte
        Falconer funded an update and expansion to dBird as part of Birds
        Connect Seattle’s Bird-safe Cities program. Thanks to their generosity,
        bird conservation organizations across North America can now manage and
        download bird mortality data within their own regions.
      </p>
      <p>
        For more information on bird collisions and to find out what you can do
        to help protect birds, please visit NYC Bird Alliance's
        <a
          href="https://nycbirdalliance.org/our-work/conservation/project-safe-flight"
          target="_blank"
          >Project Safe Flight</a
        >
        and Birds Connect Seattle’s
        <a
          href="https://birdsconnectsea.org/our-work/conservation/urban-conservation/"
          target="_blank"
          >Bird-safe Cities</a
        >.
      </p>
    </div>

    <div>
      <SectionHeader text="Create a dBird project for your organization" />
      <p>
        Would your organization like to create a dBird project and get access to
        downloadable observation data in your area? Fill out
        <router-link to="/add-your-organization">this form</router-link> and
        we'll get back to you soon!
      </p>
      <LinkButton
        type="internal"
        to="/add-your-organization"
        text="Join dBird"
      />
    </div>

    <div class="flex-row">
      <div class="column">
        <SectionHeader text="Donate to protect birds" />
        <p>
          NYC Bird Alliance's ability to protect birds and their natural areas
          in New York City relies on your support. Please consider a gift to NYC
          Bird Alliance to help support conservation efforts, like dBird, to
          make the city safer for birds and wildlife, and improve the lives of
          all New Yorkers.
        </p>
        <LinkButton
          to="https://nycbirdalliance.app.neoncrm.com/np/clients/nycbirdalliance/donation.jsp?campaign=95"
          text="Donate"
        />
      </div>
      <div class="column">
        <Photo
          source="/images/injured-northern-parula-nycba.jpg"
          caption="A Northern Parula stunned from colliding with glass."
          credit="Photo: NYC Bird Alliance"
        />
      </div>
    </div>

    <div>
      <SectionHeader text="Acknowledgements" />
      <Acknowledgement heading="Jim and Birte Falconer">
        Jim and Birte Falconer funded the 2020/2021 dBird redesign and expansion
        as part of Birds Connect Seattle’s Bird-safe Cities program.
      </Acknowledgement>

      <Acknowledgement heading="The Leon Levy Foundation">
        Leadership support for NYC Bird Alliance’s Project Safe Flight Program
        is provided by the Leon Levy Foundation.
      </Acknowledgement>

      <Acknowledgement heading="The National Fish and Wildlife Foundation">
        Funding for dBird includes a competitive grant awarded by the National
        Fish and Wildlife Foundation’s Five Star and Urban Waters Restoration
        Program, with support from the U.S. Fish and Wildlife Service.
      </Acknowledgement>

      <Acknowledgement heading="eBird">
        The species list used by dBird was sourced from
        <a href="https://ebird.org/home" target="_blank">eBird's</a> list of
        species reported in the United States. The full list can be viewed at
        <a target="blank" href="https://ebird.org/region/US"
          >https://ebird.org/region/US</a
        >.
      </Acknowledgement>
    </div>
  </InfoContentContainer>
</template>

<script>
import InfoContentContainer from "@/components/InfoContentContainer";
import Acknowledgement from "@/components/Acknowledgement";
import SectionHeader from "@/components/SectionHeader";
import Photo from "@/components/Photo";
import LinkButton from "@/components/LinkButton";

export default {
  name: "About",
  components: {
    InfoContentContainer,
    Acknowledgement,
    SectionHeader,
    Photo,
    LinkButton
  }
};
</script>

<style lang="scss" scoped>
.flex-row {
  display: flex;

  .column {
    width: 50%;

    .photo {
      float: right;
    }
  }

  @include phone {
    flex-direction: column;

    .column {
      width: 100%;

      .photo {
        display: none;
      }
    }
  }
}
</style>
